import { Tag, type TagVariantType } from "common/components/ui/Tag";
import usePromotions from "common/datahooks/usePromotions";

import PlanStatCard from "../PlanStatCard";

interface DetailCard {
  label: string;
  value: number | string;
  tagVariant: TagVariantType;
}

interface LifetimeLicenseSummaryProps {
  isSuspended?: boolean;
}

export default function LifetimeLicenseSummary({
  isSuspended = false,
}: LifetimeLicenseSummaryProps) {
  const { ltdPromotion, appSumoPromotion } = usePromotions();
  const promotion = ltdPromotion ?? appSumoPromotion;

  if (!promotion) {
    return null;
  }

  // Workspaces can hold both an "ltd" and an "appsumo" promotion at once
  // (e.g. legacy AppSumo deal plus a manually granted LTD deal), so the
  // pool totals must be summed across both rather than picking just one.
  const licenseCount =
    (ltdPromotion?.license_count ?? 0) + (appSumoPromotion?.license_count ?? 0);
  const unassignedLicenseCount =
    (ltdPromotion?.unassigned_license_count ?? 0) +
    (appSumoPromotion?.unassigned_license_count ?? 0);

  const detailCards: DetailCard[] = [
    {
      label: "Total seats",
      value: licenseCount,
      tagVariant: "tertiary",
    },
    {
      label: "Allocated",
      value: isSuspended ? "-" : licenseCount - unassignedLicenseCount,
      tagVariant: isSuspended ? "quaternary" : "secondary",
    },
    {
      label: "Available",
      value: isSuspended ? "-" : unassignedLicenseCount,
      tagVariant: isSuspended ? "quaternary" : "success",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-3">
      {detailCards.map(({ label, value, tagVariant }) => (
        <PlanStatCard title={label} key={label}>
          <Tag variant={tagVariant} size="md" className="w-fit">
            {value}
          </Tag>
        </PlanStatCard>
      ))}
    </div>
  );
}
